import * as React from 'react';
import { NavLink as RouterNavLink, useLocation } from 'react-router-dom';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { isNavGroup, navItems, type NavGroup, type NavItem, type NavLeaf } from '@/navigation/navItems';
import { SHELL_ICON_SIZE } from '@/theme';

const isPathActive = (pathname: string, to?: string) =>
  Boolean(to) && (pathname === to || pathname.startsWith(`${to}/`));

const ROW_SX = { minHeight: 40, borderRadius: 1, mx: 0.75, my: 0.25 } as const;

/**
 * The icon column, shared by both row kinds on purpose. A group row that centres its glyph
 * differently from a leaf row sits ~9px further left, which reads as an outdented section header
 * and makes the top-level items following it look like its children.
 */
const ICON_SX = { minWidth: 36, justifyContent: 'center' } as const;
/** Mini variant: the row itself centres, so the column collapses to the glyph. */
const MINI_ICON_SX = { minWidth: 0, justifyContent: 'center' } as const;

interface LeafProps {
  item: NavLeaf;
  collapsed: boolean;
  indent?: boolean;
  onNavigate?: () => void;
}

function Leaf({ item, collapsed, indent = false, onNavigate }: LeafProps) {
  const { pathname } = useLocation();
  const active = isPathActive(pathname, item.to);
  const icon = <ListItemIcon sx={collapsed ? MINI_ICON_SX : ICON_SX}>{item.icon}</ListItemIcon>;
  const text = collapsed ? null : (
    <ListItemText primary={item.label} slotProps={{ primary: { noWrap: true, sx: { fontSize: '0.875rem' } } }} />
  );
  const sx = {
    ...ROW_SX,
    pl: indent && !collapsed ? 4.5 : collapsed ? 1.5 : 2,
    justifyContent: collapsed ? 'center' : 'flex-start',
  };

  const button = item.to ? (
    <ListItemButton id={item.id} component={RouterNavLink} to={item.to} selected={active} onClick={onNavigate} sx={sx}>
      {icon}
      {text}
    </ListItemButton>
  ) : (
    <ListItemButton id={item.id} component='a' href={item.href} target='_blank' rel='noopener noreferrer' sx={sx}>
      {icon}
      {text}
    </ListItemButton>
  );

  return collapsed ? (
    <Tooltip title={item.label} placement='right'>
      {button}
    </Tooltip>
  ) : (
    button
  );
}

interface GroupProps {
  group: NavGroup;
  collapsed: boolean;
  onNavigate?: () => void;
}

function Group({ group, collapsed, onNavigate }: GroupProps) {
  const { pathname } = useLocation();
  const containsActive = group.children.some((child) => isPathActive(pathname, child.to));
  const [open, setOpen] = React.useState(containsActive);
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);

  if (collapsed) {
    const menuId = `${group.id}-menu`;
    return (
      <>
        <Tooltip title={group.label} placement='right'>
          <ListItemButton
            id={group.id}
            selected={containsActive}
            aria-haspopup='menu'
            aria-controls={anchor ? menuId : undefined}
            aria-expanded={anchor ? true : undefined}
            onClick={(event) => setAnchor(event.currentTarget)}
            sx={{ ...ROW_SX, px: 1.5, justifyContent: 'center' }}
          >
            <ListItemIcon sx={MINI_ICON_SX}>{group.icon}</ListItemIcon>
          </ListItemButton>
        </Tooltip>
        <Menu
          id={menuId}
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          sx={{ '& .MuiSvgIcon-root': { fontSize: SHELL_ICON_SIZE } }}
        >
          {group.children.map((child) => (
            <MenuItem
              key={child.id}
              component={RouterNavLink}
              to={child.to ?? '#'}
              selected={isPathActive(pathname, child.to)}
              onClick={() => {
                setAnchor(null);
                onNavigate?.();
              }}
            >
              <ListItemIcon>{child.icon}</ListItemIcon>
              <ListItemText primary={child.label} />
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  return (
    <>
      <ListItemButton
        id={group.id}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        sx={{ ...ROW_SX, pl: 2 }}
      >
        <ListItemIcon sx={ICON_SX}>{group.icon}</ListItemIcon>
        <ListItemText primary={group.label} slotProps={{ primary: { noWrap: true, sx: { fontSize: '0.875rem' } } }} />
        {open ? <ExpandLess fontSize='small' /> : <ExpandMore fontSize='small' />}
      </ListItemButton>
      <Collapse in={open} timeout='auto' unmountOnExit>
        <List disablePadding dense>
          {group.children.map((child) => (
            <Leaf key={child.id} item={child} collapsed={false} indent onNavigate={onNavigate} />
          ))}
        </List>
      </Collapse>
    </>
  );
}

export interface AppSidebarNavProps {
  /** Mini-variant: icons only, labels in tooltips, groups as menus. */
  collapsed?: boolean;
  /** Called after a leaf is chosen (used to close the mobile drawer). */
  onNavigate?: () => void;
  items?: NavItem[];
}

export default function AppSidebarNav({ collapsed = false, onNavigate, items = navItems }: AppSidebarNavProps) {
  return (
    <List
      component='nav'
      aria-label='Main navigation'
      dense
      disablePadding
      sx={{ py: 0.5, '& .MuiSvgIcon-root': { fontSize: SHELL_ICON_SIZE } }}
    >
      {items.map((item) =>
        isNavGroup(item) ? (
          <Group key={item.id} group={item} collapsed={collapsed} onNavigate={onNavigate} />
        ) : (
          <Leaf key={item.id} item={item} collapsed={collapsed} onNavigate={onNavigate} />
        )
      )}
    </List>
  );
}
